import { FaRegClock } from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Card, InputGroup, Placeholder } from "react-bootstrap";
import { rsvpSchema } from "../validators/validatorSchema";
import { clearError, validateData } from "../validators/YupValidations";
import SelectField from "../components/common/SelectField";
import { useDispatch, useSelector } from "react-redux";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import { toastErrorMessage, toastSuccessMessage } from "../utils/utils";
import { useNavigate } from "react-router-dom";
// import { formatToUSDateTime } from "../utils/dateFormat";
import { formatUSPhone, normalizePhoneForSave } from "../utils/phoneFormat";
import { formatEventDate } from "../utils/formatEventDate";
import { SET_LOADING } from "../redux/user";
import EmailVerificationModal from "../components/auth/EmailVerificationModal";
import { useLocation } from "react-router-dom";
import PaymentStatusModal from "../components/common/PaymentStatusModal";

const Rsvp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isGuestFlow = queryParams.get("guest") === "true";

  const { user } = useSelector((state) => state.USER_DATA);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', or 'cancelled'

  // console.log("user", user, "isGuestFlow", isGuestFlow);

  const IMG_PATH = process.env.REACT_APP_IMAGE_BASEURL || "";
  // console.log("user", user)

  const [formData, setFormData] = useState({
    event: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    countryCode: "+1",
    phone: "",
  });
  const [guestList, setGuestList] = useState([]);
  const [guestErrors, setGuestErrors] = useState([]);
  const [showGuestSection, setShowGuestSection] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  const [eventDetailsLoading, setEventDetailsLoading] = useState(false);

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  const eventOptions = events.map((item) => ({
    value: item._id,
    label: item.title,
  }));

  useEffect(() => {
    // console.log("Setting user data in RSVP form:", user);
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email || "",
        first_name: user?.first_name || "",
        middle_name: user?.middle_name || "",
        last_name: user?.last_name || "",
        phone: user.country_code === "+1" ? formatUSPhone(user.phone || "") : user.phone || "",
        countryCode: user.country_code,
      }));
    }
  }, [user]);

  /* ---------------- GUEST LOGIC ---------------- */

  const openGuestSection = () => {
    if (!showGuestSection) {
      setShowGuestSection(true);
      setGuestList([""]);
    }
  };

  const addGuest = () => {
    // Prevent adding a new guest if the last one is empty
    if (guestList.length > 0 && guestList[guestList.length - 1].trim() === "") {
      setGuestErrors((prev) => {
        const updated = [...prev];
        updated[guestList.length - 1] = "Full name is required";
        return updated;
      });
      return;
    }
    setShowGuestSection(true);
    // add new guest input and focus it after render
    setGuestList((prev) => {
      const next = [...prev, ""];
      // focus the newly added input on next tick
      setTimeout(() => {
        const inputs = document.querySelectorAll(".guest-row input");
        if (inputs && inputs.length) {
          inputs[inputs.length - 1].focus();
        }
      }, 0);
      return next;
    });
    setGuestErrors((prev) => [...prev, ""]);
  };

  const removeGuest = (index) => {
    setGuestList((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setShowGuestSection(false);
      return updated;
    });
    setGuestErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuestChange = (value, index) => {
    setGuestList((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    // Clear error when user starts typing
    if (guestErrors[index]) {
      setGuestErrors((prev) => {
        const updated = [...prev];
        updated[index] = "";
        return updated;
      });
    }
  };

  const handleGuestKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If this is the last input, add a new guest (same behavior as + Add Guest)
      if (index === guestList.length - 1) {
        addGuest();
      } else {
        // move focus to next guest input if present
        const inputs = document.querySelectorAll(".guest-row input");
        if (inputs && inputs[index + 1]) inputs[index + 1].focus();
      }
    }
  };

  const formForValidation = {
    ...formData,
    phone:
      formData.countryCode === "+1"
        ? normalizePhoneForSave(formData.phone)
        : formData.phone.replace(/\D/g, ""),
  };

  /* ---------------- CALCULATIONS ---------------- */

  const validGuests = guestList.filter((g) => g.trim() !== "");
  // Primary is always included in this form flow
  const totalTickets = 1 + validGuests.length;
  const totalAmount = (totalTickets * (Number(eventDetails?.price) || 0)).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // IF LOGGED IN -> Redirect to Event Details to Reserve
    // Skip this if isGuestFlow is true
    if (user && !isGuestFlow) {
      navigate(`/events/${formData.event}`);
      return;
    }

    const isValid = await validateData(rsvpSchema, formForValidation, setErrors);
    if (!isValid) return;

    if (!isEmailVerified) {
      toastErrorMessage("Please verify your email before proceeding.");
      return;
    }

    // IF NOT LOGGED IN -> PROCEED TO PAYMENT
    try {
      dispatch(SET_LOADING(true));
      const payload = {
        amount: eventDetails?.price
          ? (1 + guestList.filter((g) => g.trim()).length) * Number(eventDetails.price)
          : 0, // Placeholder calculation, backend verifies
        event_id: formData.event,
        primary_included: true,
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formForValidation.phone,
        country_code: formData.countryCode,
        guests: guestList.filter((g) => g.trim() !== "").map((g) => ({ name: g })),
      };

      console.log("Submitting Guest RSVP with data:", payload);

      const response = await API.post(
        `${api_paths.member.payment.EVENT_CHECKOUT}?type=event&screen=rsvp`,
        payload
      );

      console.log("RSVP Response:", response.data);

      if (response.data?.payload?.token) {
        const token = response.data.payload.token;
        // Submit to Converge
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);
        dispatch(SET_LOADING(false));
        form.submit();
      } else if (response.data?.payload?.result?.success) {
        toastSuccessMessage("Ticket booked successfully!");
        dispatch(SET_LOADING(false));
        navigate("/");
      }
    } catch (error) {
      toastErrorMessage(
        error?.response?.data?.message || "Failed to submit RSVP. Please try again."
      );
      dispatch(SET_LOADING(false));
      // console.error("Error submitting RSVP:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }

    // console.log("RSVP form submitted with valid data.");
  };

  // Handle closing the modal and cleaning the URL
  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate(`/rsvp`, { replace: true });
  };

  const handleRedirect = () => {
    setPaymentStatus(null);
    // Remove query params from URL without reloading
    navigate("/rsvp", { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const convergeId = params.get("converge_transaction_id");

    if (status) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  useEffect(() => {
    const fetchAvailableEvents = async () => {
      try {
        const response = await API.get(api_paths.member.EVENTS.GET_LIST);
        // console.log("Events List:", response.data);

        const eventList = response.data?.payload?.result || [];
        setEvents(eventList);

        if (eventList.length > 0) {
          setFormData((prev) => ({
            ...prev,
            event: eventList[0]._id,
          }));
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    // console.log("User Data: ", user);

    fetchAvailableEvents();
  }, []);

  useEffect(() => {
    if (!formData.event) return;

    const fetchEvent = async (eventId) => {
      try {
        setEventDetailsLoading(true);
        const response = await API.get(`${api_paths.member.EVENTS.EVENT}/${eventId}`);
        // console.log("Event Details:", response.data);

        const eventData = response.data?.payload?.result || null;
        setEventDetails(eventData);
        // console.log("Event data ha been set to: ", eventData);
        setEventDetailsLoading(false);
      } catch (error) {
        // console.error("Error fetching event details:", error);
        setEventDetailsLoading(false);
      }
    };

    fetchEvent(formData.event);
  }, [formData.event]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      setFormData({
        event: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        countryCode: "+1",
        phone: "",
      });

      setErrors({});
    };
  }, []);

  return (
    <div>
      <Container className="py-5">
        <Row className="mb-4">
          <Col lg={12}>
            <div className="rsvp-header d-flex flex-column justify-content-center align-items-center text-center">
              <h1 className="inter-bold h1">Event RSVP</h1>
              <p>
                {user && !isGuestFlow ? "Register " : "Guest can register "}
                for upcoming community events and celebrations
              </p>
            </div>
          </Col>
        </Row>

        {/* Two-column section */}
        <Row className="gy-4 flex-column-reverse flex-lg-row">
          {/* LEFT: Form */}
          <Col lg={6}>
            <Card className="rsvp-form p-md-3 p-1">
              <Card.Body>
                <Card.Title className="fw-bold">Register Your Attendance</Card.Title>
                <Card.Text>
                  Fill out the form below to confirm your attendance at an upcoming event
                </Card.Text>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="formEvent">
                    <SelectField
                      label="Select Event"
                      required
                      value={eventOptions.find((opt) => opt.value === formData.event) || null}
                      options={eventOptions}
                      onChange={(opt) => {
                        setFormData({
                          ...formData,
                          event: opt ? opt.value : "",
                        });
                        errors.event && clearError(setErrors, "event");
                      }}
                      error={errors.event?.message}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Group controlId="formFirstName">
                        <Form.Label>First Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="First Name"
                          value={formData.first_name}
                          onChange={(e) => {
                            setFormData({ ...formData, first_name: e.target.value });
                            errors.first_name && clearError(setErrors, "first_name");
                          }}
                          disabled={!!user && !isGuestFlow}
                        />
                        {errors.first_name && (
                          <Form.Text className="text-danger">{errors.first_name.message}</Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group controlId="formMiddleName">
                        <Form.Label>Middle Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Middle Name"
                          value={formData.middle_name}
                          onChange={(e) =>
                            setFormData({ ...formData, middle_name: e.target.value })
                          }
                          disabled={!!user && !isGuestFlow}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group controlId="formLastName">
                        <Form.Label>Last Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Last Name"
                          value={formData.last_name}
                          onChange={(e) => {
                            setFormData({ ...formData, last_name: e.target.value });
                            errors.last_name && clearError(setErrors, "last_name");
                          }}
                          disabled={!!user && !isGuestFlow}
                        />
                        {errors.last_name && (
                          <Form.Text className="text-danger">{errors.last_name.message}</Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Email Address *</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          errors.email && clearError(setErrors, "email");
                          if (isEmailVerified) setIsEmailVerified(false); // reset if changed
                        }}
                        disabled={(!!user && !isGuestFlow) || isEmailVerified}
                      />
                      {(!user || isGuestFlow) && (
                        <Button
                          // variant={isEmailVerified ? "success" : "secondary"}
                          className={`btn ${isEmailVerified ? "btn-black" : "btn-orange"}`}
                          disabled={isEmailVerified}
                          onClick={() => setShowVerifyModal(true)}
                        >
                          {isEmailVerified ? "Verified" : "Verify"}
                        </Button>
                      )}
                    </div>
                    {errors.email && (
                      <Form.Text className="text-danger">{errors.email.message}</Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="formPhone">
                    <Form.Label>Phone Number *</Form.Label>

                    <Row className="g-2">
                      <Col xs={4} md={3}>
                        <SelectField
                          value={countryCodeOptions.find(
                            (opt) => opt.value === formData.countryCode
                          )}
                          options={countryCodeOptions}
                          onChange={(opt) => {
                            const newCode = opt.value;

                            setFormData((prev) => ({
                              ...prev,
                              countryCode: newCode,
                              phone:
                                newCode === "+1"
                                  ? formatUSPhone(normalizePhoneForSave(prev.phone))
                                  : prev.phone.replace(/\D/g, ""),
                            }));

                            errors.phone && clearError(setErrors, "phone");
                          }}
                          disabled={!!user && !isGuestFlow}
                        />
                      </Col>

                      <Col xs={8} md={9}>
                        <Form.Control
                          type="tel"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={(e) => {
                            let value = e.target.value;

                            if (formData.countryCode === "+1") {
                              value = formatUSPhone(value).slice(0, 15);
                            } else {
                              value = value.replace(/\D/g, "").slice(0, 10);
                            }
                            setFormData({ ...formData, phone: value });
                            errors.phone && clearError(setErrors, "phone");
                          }}
                          maxLength={formData.countryCode === "+1" ? 15 : 10}
                          disabled={!!user && !isGuestFlow}
                        />
                      </Col>
                    </Row>

                    {errors.phone && (
                      <Form.Text className="text-danger">{errors.phone.message}</Form.Text>
                    )}
                  </Form.Group>

                  {(!user || isGuestFlow) && (
                    <>
                      <div className="mb-2">
                        <label
                          onClick={openGuestSection}
                          style={{
                            display: "block",
                            cursor: "pointer",
                            fontWeight: 600,
                            color: "#E69F00", // label color
                            paddingBottom: 8,
                            fontSize: "15px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <div className="icon-plus me-2">+</div>
                          <div className="mt-1"> Add Guests Tickets</div>
                        </label>

                        {showGuestSection && (
                          <>
                            {guestList.map((g, i) => (
                              <div className="guest-row" key={i}>
                                <div className="guest-input-wrapper w-100 mb-2">
                                  <input
                                    className="form-control"
                                    placeholder="Full Name"
                                    value={g}
                                    onChange={(e) => handleGuestChange(e.target.value, i)}
                                    onKeyDown={(e) => handleGuestKeyDown(e, i)}
                                    style={{
                                      border: guestErrors[i]
                                        ? "1px solid #dc3545"
                                        : "1px solid var(--border-color)",
                                    }}
                                  />

                                  <button
                                    type="button"
                                    className="guest-remove-btn"
                                    onClick={() => removeGuest(i)}
                                    aria-label="remove guest"
                                  >
                                    ✕
                                  </button>

                                  {guestErrors[i] && (
                                    <small className="text-danger d-block mt-1">
                                      {guestErrors[i]}
                                    </small>
                                  )}
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={addGuest}
                              style={{
                                width: "100%",
                                background: "var(--border-color)",
                                border: "1px solid var(--border-color)",
                                padding: "12px",
                                borderRadius: "8px",
                                border: "none",
                                fontWeight: 500,
                                marginBottom: "15px",
                                cursor: "pointer",
                                color: "var(--text-main)",
                                fontSize: "14px",
                              }}
                            >
                              + Add Guest
                            </button>
                          </>
                        )}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Total Tickets</span>
                        <strong>{totalTickets}</strong>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <span>Total Amount</span>
                        <strong>${totalAmount}</strong>
                      </div>
                    </>
                  )}

                  <Button
                    type="submit"
                    className="btn  btn-orange-md px-4 border-0"
                    disabled={(!user || isGuestFlow) && !isEmailVerified}
                  >
                    RSVP & Purchase Ticket
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* RIGHT: Details card */}
          <Col lg={6}>
            {eventDetailsLoading ? (
              <Card className="shadow-sm h-100">
                {/* Image skeleton */}
                <div className="w-100 bg-light placeholder-glow" style={{ height: "200px" }}>
                  <span className="placeholder w-100 h-100"></span>
                </div>

                <Card.Body>
                  {/* Title */}
                  <Placeholder as={Card.Title} animation="wave" className="w-75">
                    <Placeholder xs={9} />
                  </Placeholder>

                  {/* Price */}
                  <Placeholder as={Card.Text} animation="wave" className="w-50">
                    <Placeholder xs={6} />
                  </Placeholder>

                  {/* Description */}
                  <Placeholder as={Card.Text} animation="wave">
                    <Placeholder xs={12} />
                    <Placeholder xs={10} />
                    <Placeholder xs={8} />
                  </Placeholder>

                  {/* Time */}
                  <Placeholder as={Card.Text} animation="wave" className="w-50">
                    <Placeholder xs={7} />
                  </Placeholder>

                  {/* Location */}
                  <Placeholder as={Card.Text} animation="wave" className="w-75">
                    <Placeholder xs={9} />
                  </Placeholder>
                </Card.Body>
              </Card>
            ) : eventDetails ? (
              <Card className="rsvp-card h-100">
                {eventDetails?.image_path && (
                  <Card.Img
                    variant="top"
                    className="w-100 rsvp-card-img"
                    src={`${IMG_PATH}${eventDetails?.image_path}`}
                  />
                )}
                <Card.Body>
                  <Card.Title className="fw-bold rsvp-detail-title">
                    {eventDetails?.title || "N/A"}
                  </Card.Title>
                  <Card.Text className="rsvp-detail-subtitle fw-medium f-16">
                    <>
                      <Card.Text className="ticket-price mb-2 fw-medium f-16">
                        Non-Members ${Number(eventDetails?.price).toFixed(2)}
                      </Card.Text>
                      <Card.Text className="event-overlay-member-price">
                        {" "}
                        {user?.member_price
                          ? ` ${new Date(eventDetails?.start_date).getFullYear()} Members: ${
                              user?.member_price
                            }`
                          : `Free to ${new Date(eventDetails?.start_date).getFullYear()} Members`}
                      </Card.Text>
                    </>
                  </Card.Text>
                  <Card.Text>{eventDetails?.description || "No description available."}</Card.Text>
                  <Card.Text className="d-flex align-items-center">
                    <FaRegClock size={16} />
                    <span className="ms-2">
                      <span>{formatEventDate(eventDetails)}</span>
                    </span>
                  </Card.Text>
                  <Card.Text>
                    <HiOutlineLocationMarker size={16} />
                    <span className="ms-2">{eventDetails?.venue || "N/A"}</span>
                  </Card.Text>
                </Card.Body>
              </Card>
            ) : (
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title className="fw-bold" style={{ paddingTop: "8px" }}>
                    Event Details
                  </Card.Title>

                  <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                    <Card.Text className="text-muted">
                      No details available for the selected event.
                    </Card.Text>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      <EmailVerificationModal
        show={showVerifyModal}
        onHide={() => setShowVerifyModal(false)}
        email={formData.email}
        onVerified={() => setIsEmailVerified(true)}
      />

      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus?.status}
          convergeId={paymentStatus?.convergeId}
          onClose={handleCloseModal}
          onNavigate={handleRedirect}
          buttonName={"event"}
          screen={"rsvp"}
          type={"event"}
        />
      )}
    </div>
  );
};

export default Rsvp;
